import * as React from "react";
import { useNavigate } from "react-router";
import { CircleAlert } from "lucide-react";
import { Button, Input, Label, Wordmark } from "@moanhot/ui";
import { AdminProblem, useAdminApi } from "@/api/adminClient";

/** Operator sign-in: login and password, one card on the paper canvas. */
export function SignIn() {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  return (
    <div className="flex min-h-dvh flex-col items-center bg-canvas px-4 pt-[12vh] pb-12 sm:justify-center sm:pt-12">
      <main className="w-full max-w-[380px]">
        <div className="flex justify-center">
          <Wordmark suffix="Admin" />
        </div>
        <form
          className="mt-8 rounded-lg border border-line bg-raised px-5 pt-6 pb-5 sm:px-7 sm:pt-7 sm:pb-7"
          aria-label="Operator sign in"
          aria-busy={busy || undefined}
          onSubmit={(e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            const value = (k: string) => ((data.get(k) as string | null) ?? "").trim();
            setBusy(true);
            setProblem(null);
            api
              .login({ login: value("login"), password: (data.get("password") as string | null) ?? "" })
              .then(() => navigate("/applications", { replace: true }))
              .catch((err: unknown) => {
                setProblem(err instanceof AdminProblem ? err.detail : "Could not reach the server. Try again.");
                setBusy(false);
              });
          }}
        >
          <h1 className="text-lg font-medium tracking-title">Operator sign in</h1>
          <p className="mt-1 text-sm text-ink-2">Use the login you were given.</p>
          <div className="mt-6 space-y-1.5">
            <Label htmlFor="op-login">Login</Label>
            <Input id="op-login" name="login" className="h-10" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required maxLength={64} />
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="op-password">Password</Label>
            <Input id="op-password" name="password" type="password" className="h-10" autoComplete="current-password" required maxLength={256} />
          </div>
          {problem && (
            <p role="alert" className="mt-4 flex gap-2 text-sm text-err">
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{problem}</span>
            </p>
          )}
          <Button type="submit" variant="primary" size="lg" className="mt-6 w-full" loading={busy}>
            {busy ? "Signing in" : "Sign in"}
          </Button>
        </form>
        <p className="mt-6 text-center text-xs text-ink-3">For moan.hot staff. Partners sign in from the dashboard.</p>
      </main>
    </div>
  );
}
